import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Loader2,
  PlayCircle,
  RefreshCw,
  Search,
  Zap,
} from "lucide-react";
import { mpHealthApi } from "./api";
import type { PendingTicket, SweepSummary } from "./types";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import { formatDateTime } from "../applications/shared/format";
import { useToast } from "@/app/providers/Toaster";
import { confirmAction } from "@/app/providers/ConfirmDialog";

const PAGE_SIZE = 25;

export function MpHealthScreen() {
  const [sweepWindow, setSweepWindow] = useState(0);
  const [pendingWindow, setPendingWindow] = useState(10);
  const [reconcileTxId, setReconcileTxId] = useState("");
  const [page, setPage] = useState(1);
  const [lastSweep, setLastSweep] = useState<SweepSummary | null>(null);
  const [lastReconcile, setLastReconcile] = useState<{ tx: number; ok: boolean; message: string } | null>(null);

  const qc = useQueryClient();
  const { push } = useToast();

  const pendingQuery = useQuery({
    queryKey: ["operations", "mp-pending", { olderThanMinutes: pendingWindow }],
    queryFn: () => mpHealthApi.listPending(pendingWindow),
  });

  const forceSweep = useMutation({
    mutationFn: () => mpHealthApi.forceSweep(sweepWindow),
    onSuccess: (res) => {
      setLastSweep(res);
      const total = res.scanned;
      push({
        kind: "success",
        message:
          total === 0
            ? "Sweep completo. No había pendientes."
            : `Sweep completo. Escaneados: ${total} · Reconciliados: ${res.reconciled} · Errores: ${res.errors}`,
      });
      qc.invalidateQueries({ queryKey: ["operations", "mp-pending"] });
    },
    onError: (err: any) => {
      push({ kind: "error", message: err?.response?.data?.message || "Error en sweep." });
    },
  });

  const reconcileById = useMutation({
    mutationFn: (txId: number) => mpHealthApi.reconcileById(txId),
    onSuccess: (res) => {
      setLastReconcile({ tx: res.transactionId, ok: true, message: res.message });
      push({ kind: "success", message: res.message });
      qc.invalidateQueries({ queryKey: ["operations", "mp-pending"] });
    },
    onError: (err: any) => {
      const txId = Number(reconcileTxId);
      const message = err?.response?.data?.message || "Error reconciliando.";
      setLastReconcile({ tx: txId, ok: false, message });
      push({ kind: "error", message });
    },
  });

  return (
    <div className="flex flex-col gap-6 animate-fade-in">
      <header>
        <p className="nx-eyebrow mb-1">Operaciones · Salud MP</p>
        <h1 className="nx-h1">Salud MercadoPago</h1>
        <p className="text-sm text-fg-muted mt-1">
          Panel manual para destrabar el flow MP cuando un webhook se pierde o
          un cron falla. Las 3 acciones son idempotentes: si ya están
          procesados, no duplican tickets.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Card 1 — Forzar sweep */}
        <div className="nx-card p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-accent" />
            <h2 className="text-sm font-semibold text-fg">Forzar sweep ahora</h2>
          </div>
          <p className="text-2xs text-fg-subtle">
            Escanea transacciones PENDING (ventana en minutos) y crea tickets si
            el pago está APPROVED en MP. 0 = todas, sin importar antigüedad.
          </p>
          <label className="nx-eyebrow">Ventana (min)</label>
          <input
            type="number"
            min={0}
            max={1440}
            value={sweepWindow}
            onChange={(e) => setSweepWindow(parseInt(e.target.value) || 0)}
            className="nx-input"
          />
          <button
            onClick={async () => {
              const ok = await confirmAction({
                title: "Forzar sweep manual",
                message: `Escanea pendientes con más de ${sweepWindow} minutos. Idempotente, pero consulta API de MP por cada transacción.`,
                confirmLabel: "Disparar sweep",
              });
              if (ok === true) forceSweep.mutate();
            }}
            disabled={forceSweep.isPending}
            className="nx-btn-primary"
          >
            {forceSweep.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <PlayCircle size={14} />
            )}
            Disparar sweep
          </button>
          {lastSweep && <SweepSummaryCard summary={lastSweep} />}
        </div>

        {/* Card 2 — Listar pendientes */}
        <div className="nx-card p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Search size={16} className="text-info" />
            <h2 className="text-sm font-semibold text-fg">Listar pendientes</h2>
          </div>
          <p className="text-2xs text-fg-subtle">
            Tickets en estado <code>Disponible</code> con QR <code>PENDING_*</code>{" "}
            más antiguos que la ventana. Indica pagos huérfanos.
          </p>
          <label className="nx-eyebrow">Ventana (min)</label>
          <input
            type="number"
            min={0}
            max={1440}
            value={pendingWindow}
            onChange={(e) => {
              setPendingWindow(parseInt(e.target.value) || 0);
              setPage(1);
            }}
            className="nx-input"
          />
          <button
            onClick={() => pendingQuery.refetch()}
            disabled={pendingQuery.isFetching}
            className="nx-btn-secondary"
          >
            <RefreshCw size={14} className={pendingQuery.isFetching ? "animate-spin" : ""} />
            Recargar lista
          </button>
          <div className="text-2xs text-fg-subtle text-center pt-1">
            {pendingQuery.isFetching ? (
              <Loader2 size={12} className="inline animate-spin" />
            ) : (
              <>
                Encontrados:{" "}
                <span className="font-semibold text-fg tabular-nums">
                  {pendingQuery.data?.count ?? 0}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Card 3 — Reconciliar por ID */}
        <div className="nx-card p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-warning" />
            <h2 className="text-sm font-semibold text-fg">Reconciliar por TX ID</h2>
          </div>
          <p className="text-2xs text-fg-subtle">
            Forzar reconciliación de una transacción específica. Útil cuando
            tenés un caso 1-a-1 reportado por un usuario.
          </p>
          <label className="nx-eyebrow">Transaction ID</label>
          <input
            type="text"
            value={reconcileTxId}
            onChange={(e) => setReconcileTxId(e.target.value.replace(/\D/g, ""))}
            placeholder="ej. 12345"
            className="nx-input"
          />
          <button
            onClick={async () => {
              const txId = parseInt(reconcileTxId, 10);
              if (!Number.isFinite(txId) || txId <= 0) {
                push({ kind: "error", message: "Ingresá un transaction ID válido." });
                return;
              }
              const ok = await confirmAction({
                title: `Reconciliar transacción #${txId}`,
                message:
                  "Consulta MP por external_reference y crea/actualiza los tickets si hay pago APPROVED.",
                confirmLabel: "Reconciliar",
              });
              if (ok === true) reconcileById.mutate(txId);
            }}
            disabled={reconcileById.isPending}
            className="nx-btn-secondary"
          >
            {reconcileById.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Zap size={14} />
            )}
            Reconciliar
          </button>
          {lastReconcile && (
            <div
              className={`text-2xs p-2 rounded border ${
                lastReconcile.ok
                  ? "border-success/40 bg-success-soft text-fg"
                  : "border-danger/40 bg-danger-soft text-fg"
              }`}
            >
              <strong>Tx #{lastReconcile.tx}:</strong> {lastReconcile.message}
            </div>
          )}
        </div>
      </div>

      {/* Tabla pendientes */}
      <section>
        <h2 className="nx-h2 mb-3">Pendientes detectados</h2>
        {pendingQuery.isLoading ? (
          <LoadingBlock label="Cargando pendientes…" />
        ) : pendingQuery.isError ? (
          <ErrorBlock onRetry={() => pendingQuery.refetch()} />
        ) : (
          <ApplicationsTable<PendingTicket & { id: number }>
            rows={(pendingQuery.data?.pending ?? []).map((p) => ({ ...p, id: p.ticketId }))}
            columns={columns(setReconcileTxId)}
            page={page}
            pageSize={PAGE_SIZE}
            total={pendingQuery.data?.count ?? 0}
            onPageChange={setPage}
            emptyLabel="Sin pendientes detectados."
          />
        )}
      </section>
    </div>
  );
}

function SweepSummaryCard({ summary }: { summary: SweepSummary }) {
  const items = [
    { label: "Escaneados", value: summary.scanned },
    { label: "Reconciliados", value: summary.reconciled, tone: "success" as const },
    { label: "Ya procesados", value: summary.alreadyProcessed, tone: "info" as const },
    { label: "Sin pago", value: summary.noPayment },
    { label: "Errores", value: summary.errors, tone: summary.errors > 0 ? ("danger" as const) : undefined },
  ];
  return (
    <div className="grid grid-cols-5 gap-1 mt-1">
      {items.map((it) => (
        <div
          key={it.label}
          className={`text-2xs text-center p-1.5 rounded ${
            it.tone === "success"
              ? "bg-success-soft text-success"
              : it.tone === "info"
                ? "bg-info-soft text-info"
                : it.tone === "danger"
                  ? "bg-danger-soft text-danger"
                  : "bg-bg-subtle text-fg-muted"
          }`}
        >
          <div className="font-bold tabular-nums">{it.value}</div>
          <div className="text-[10px] mt-0.5">{it.label}</div>
        </div>
      ))}
    </div>
  );
}

function columns(onReconcileSelect: (id: string) => void): Column<PendingTicket & { id: number }>[] {
  return [
    {
      key: "ticketId",
      header: "Ticket ID",
      cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.ticketId}</span>,
      width: "min",
    },
    {
      key: "transactionId",
      header: "Tx ID",
      cell: (r) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (r.transactionId) onReconcileSelect(String(r.transactionId));
          }}
          className="text-accent hover:underline text-xs tabular-nums"
          title="Cargar en card de reconciliar"
        >
          #{r.transactionId ?? "—"}
        </button>
      ),
      width: "min",
    },
    {
      key: "event",
      header: "Evento",
      cell: (r) => (
        <div>
          <div className="text-fg text-xs">{r.eventNombre ?? "—"}</div>
          <div className="text-2xs text-fg-subtle">{r.tipoTicketNombre ?? "—"}</div>
        </div>
      ),
    },
    {
      key: "user",
      header: "Usuario",
      cell: (r) => (
        <div>
          <div className="text-fg text-xs">{r.usuarioNombre ?? "—"}</div>
          <div className="text-2xs text-fg-subtle break-all">{r.usuarioEmail ?? "—"}</div>
        </div>
      ),
    },
    {
      key: "txCreated",
      header: "Tx creada",
      cell: (r) => (
        <span className="text-fg-subtle text-xs">
          {formatDateTime(r.transactionCreatedAt)}
        </span>
      ),
      width: "min",
      align: "right",
    },
  ];
}
